import { Individual } from "@semantical/core/modules/knowledge-graph";
import { SchemaIndividual } from "@semantical/core/modules/ontology-model";
import { isIndividual } from "@semantical/core/modules/workspace";
export default class DiagramNode {
    constructor(modelElement, x, y, width, height, hidden = false, origin) {
        Object.defineProperty(this, "x", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "y", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "width", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "height", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "modelElement", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        }); // Can be SchemaClass (TBox), Individual (ABox), or SchemaIndividual (TBox)
        Object.defineProperty(this, "hidden", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        }); // Whether this node is hidden from the diagram view
        Object.defineProperty(this, "origin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        }); // Where this element came from (KG or Ontology)
        this.modelElement = modelElement;
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.hidden = hidden;
        this.origin = origin;
    }
    /**
     * Check if this DiagramNode has a placeholder SchemaIndividual (missing class info).
     * This happens when the diagram was restored before the ontology was loaded.
     */
    isPlaceholder() {
        return this.modelElement instanceof SchemaIndividual &&
            this.modelElement.classUris.length === 0;
    }
    /**
     * @deprecated Ontology content is now server-side only.
     * Placeholder resolution is handled by DiagramViewModel.
     * This method is kept for backward compatibility but does nothing.
     */
    resolveFromBoundedContext(_boundedContext) {
        // Ontology content is server-side only - no client-side resolution
        return false;
    }
    toJSON() {
        const isSchemaIndividual = this.modelElement instanceof SchemaIndividual;
        const isABoxIndividual = this.modelElement instanceof Individual;
        // Determine element type: schema-individual (TBox), individual (ABox), or class
        let elementType;
        if (isSchemaIndividual) {
            elementType = 'schema-individual';
        }
        else if (isABoxIndividual) {
            elementType = 'individual';
        }
        else {
            elementType = 'class';
        }
        // For schema-individual, also store classUris so stereotype can display before ontology loads
        const classUris = isSchemaIndividual && isIndividual(this.modelElement) && this.modelElement.classUris.length > 0
            ? this.modelElement.classUris
            : undefined;
        return {
            x: this.x,
            y: this.y,
            width: this.width,
            height: this.height,
            hidden: this.hidden || undefined, // Only include if true
            modelElement: {
                href: this.modelElement.uri,
                type: elementType,
                origin: this.origin,
                classUris, // Store class URIs for stereotype resolution
            }
        };
    }
    /**
     * Extract a readable label from a URI (last segment after # or /)
     */
    static extractLabelFromUri(uri) {
        const hashIndex = uri.lastIndexOf('#');
        if (hashIndex !== -1)
            return uri.substring(hashIndex + 1);
        const slashIndex = uri.lastIndexOf('/');
        if (slashIndex !== -1)
            return uri.substring(slashIndex + 1);
        return uri;
    }
    /**
     * Create a DiagramNode from JSON.
     * @param json - The serialized node data
     * @param knowledgeGraph - The KnowledgeGraph to look up ABox individuals (legacy, no longer used server-side)
     * @param _boundedContext - @deprecated Ontology content is now server-side only
     */
    static fromJSON(json, knowledgeGraph, _boundedContext) {
        const elementUri = json.modelElement.href;
        const elementType = json.modelElement.type;
        // Resolve origin: new format or backward compat from legacy fields
        const origin = json.modelElement.origin ?? (json.modelElement.datasetId
            ? { type: 'kg', datasetId: json.modelElement.datasetId }
            : json.modelElement.sourceGraphIRI
                ? { type: 'ontology', ontologyIRI: json.modelElement.sourceGraphIRI, ontologyId: json.modelElement.sourceGraphIRI }
                : undefined);
        // Find the Node by its identifying URI
        let modelElement;
        if (elementType === 'schema-individual') {
            // Create a placeholder SchemaIndividual from stored data
            // Ontology content is server-side only; DiagramViewModel handles full resolution
            const label = DiagramNode.extractLabelFromUri(elementUri);
            const classUris = json.modelElement.classUris || [];
            modelElement = new SchemaIndividual(elementUri, label, classUris, '');
        }
        else if (elementType === 'individual') {
            // Look in ABox individuals collection (uri = individualUri)
            modelElement = Array.from(knowledgeGraph.individuals?.values() ?? []).find(ind => ind.uri === elementUri);
        }
        // Note: For 'class' type (TBox editing), SchemaClass handling will be added in the future.
        if (!modelElement) {
            console.debug(`DiagramNode.fromJSON: Node not found for ${elementUri}`);
            return null;
        }
        return new DiagramNode(modelElement, json.x, json.y, json.width, json.height, json.hidden ?? false, origin);
    }
}
